import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Container,
  Flex,
  Grid,
  HStack,
  Heading,
  Icon,
  IconButton,
  Input,
  Portal,
  Select,
  Separator,
  Spinner,
  Text,
  VStack,
  createListCollection,
} from "@chakra-ui/react";
import { useCallback, useState } from "react";
import { FaFacebook, FaLinkedin } from "react-icons/fa";
import { LuChevronLeft, LuChevronRight, LuSearch } from "react-icons/lu";
import { MdClear } from "react-icons/md";
import ReactSelect from "react-select";
import GlobalLoading from "../components/GlobalLoading";
import { useGetAlCountryQuery } from "../features/countryApi";
import { useGetAllMembersQuery } from "../features/memberApi";
import { useGetMembershipTypesQuery } from "../features/membershipApi";
import { MemberResponse } from "../types/MemberResponse";

interface SearchFilters {
  search: string;
  membershipTypeId: string;
  countryId: string;
  degree: string;
}

const PAGE_SIZE = 20;

function MemberCard({ member }: { member: MemberResponse }) {
  const getProfilePictureUrl = (profilePictureId?: string) => {
    if (!profilePictureId) return undefined;
    return `${
      import.meta.env.VITE_API_URL
    }/upload/download/${profilePictureId}`;
  };

  const formatDegreeAndBatch = () => {
    const degree = member.iba_degree || "";
    const batch = member.iba_batch || "";
    if (degree && batch) return `${degree}-${batch}`;
    if (degree) return degree;
    if (batch) return `${batch}`;
    return "N/A";
  };

  return (
    <Card.Root
      variant="elevated"
      _hover={{
        shadow: "lg",
        transform: "translateY(-2px)",
      }}
      transition="all 0.2s"
    >
      <Card.Body p={4}>
        <VStack gap={4} align="stretch">
          {/* Profile Picture and Basic Info */}
          <HStack gap={4}>
            <Avatar.Root shape="rounded" size="xl">
              <Avatar.Fallback name={member.full_name} />
              <Avatar.Image
                src={`${import.meta.env.VITE_API_URL}/upload/download/${
                  member?.profile_picture_id
                }`}
              />
            </Avatar.Root>
            <VStack align="start" gap={1} flex={1}>
              <Text fontWeight="semibold" fontSize="md" color="gray.800">
                {member.full_name || "N/A"}
              </Text>
              <Text fontSize="sm" color="blue.600">
                {member.club_id || "N/A"}
              </Text>
            </VStack>
          </HStack>

          <Separator />

          {/* Member Information */}
          <VStack gap={2} align="stretch">
            {/* Blood Group */}
            <HStack justify="space-between">
              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                Blood Group:
              </Text>
              <Text fontSize="xs" color="gray.800">
                {member.blood_group || "N/A"}
              </Text>
            </HStack>

            {/* Degree & Batch */}
            <HStack justify="space-between">
              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                Degree & Batch:
              </Text>
              <Text fontSize="xs" color="gray.800">
                {formatDegreeAndBatch()}
              </Text>
            </HStack>

            {/* Profession */}
            <HStack justify="space-between">
              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                Profession:
              </Text>
              <Text fontSize="xs" color="gray.800" textAlign="right">
                {member.professional_info?.profession || "N/A"}
              </Text>
            </HStack>

            {/* Designation */}
            <HStack justify="space-between">
              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                Designation:
              </Text>
              <Text fontSize="xs" color="gray.800" textAlign="right">
                {member.professional_info?.designation || "N/A"}
              </Text>
            </HStack>

            {/* Organization */}
            <HStack justify="space-between">
              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                Organization:
              </Text>
              <Text fontSize="xs" color="gray.800" textAlign="right">
                {member.professional_info?.organization || "N/A"}
              </Text>
            </HStack>

            {/* Office Phone */}
            <HStack justify="space-between">
              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                Office Phone:
              </Text>
              <Text fontSize="xs" color="gray.800">
                {member.contact_info?.office_tel || "N/A"}
              </Text>
            </HStack>

            {/* Mobile Phone */}
            <HStack justify="space-between">
              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                Mobile Phone:
              </Text>
              <Text fontSize="xs" color="gray.800">
                {member.contact_info?.mobile_tel || "N/A"}
              </Text>
            </HStack>

            {/* Email */}
            <HStack justify="space-between">
              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                Email:
              </Text>
              <Text fontSize="xs" color="gray.800" textAlign="right">
                {member.contact_info?.email || "N/A"}
              </Text>
            </HStack>
          </VStack>

          {/* Social Media Links */}
          {(member.facebook_link || member.linkedin_link) && (
            <>
              <Separator />
              <HStack justify="flex-start" gap={1}>
                {member.facebook_link && (
                  <Box
                    as="button"
                    p={1}
                    borderRadius="md"
                    cursor="pointer"
                    onClick={() => window.open(member.facebook_link, "_blank")}
                    _hover={{
                      bg: "blue.50",
                      transform: "scale(1.1)",
                    }}
                    transition="all 0.2s ease"
                  >
                    <Icon fontSize="xl" color="blue.600">
                      <FaFacebook />
                    </Icon>
                  </Box>
                )}
                {member.linkedin_link && (
                  <Box
                    as="button"
                    p={1}
                    borderRadius="md"
                    cursor="pointer"
                    onClick={() => window.open(member.linkedin_link, "_blank")}
                    _hover={{
                      bg: "blue.50",
                      transform: "scale(1.1)",
                    }}
                    transition="all 0.2s ease"
                  >
                    <Icon fontSize="xl" color="blue.700">
                      <FaLinkedin />
                    </Icon>
                  </Box>
                )}
              </HStack>
            </>
          )}
        </VStack>
      </Card.Body>
    </Card.Root>
  );
}

function MemberForAllMember() {
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<SearchFilters>({
    search: "",
    membershipTypeId: "",
    countryId: "",
    degree: "",
  });

  // RTK Query for fetching members
  const {
    data: membersData,
    isLoading,
    error,
    isFetching,
  } = useGetAllMembersQuery({
    page: currentPage,
    size: PAGE_SIZE,
    search: filters.search || undefined,
    membership_type_id: filters.membershipTypeId || undefined,
    country_id: filters.countryId || undefined,
    degree: filters.degree || undefined,
  });



  // RTK Query for fetching membership types and countries
  const { data: membershipTypes } = useGetMembershipTypesQuery();
  const { data: countriesData } = useGetAlCountryQuery();

  // Create collections for selects
  const membershipTypesCollection = createListCollection({
    items: [
      { value: "", label: "All Membership Types" },
      ...(membershipTypes?.map((type) => ({
        value: type.id,
        label: type.name,
      })) || []),
    ],
  });

  // Country options for ReactSelect
  const countryOptions = [
    { value: "", label: "All Countries" },
    ...(countriesData?.map((country) => ({
      value: country.id, // Already a string, no need to convert
      label: country.name,
    })) || []),
  ];

  // Degree collection
  const degreeCollection = createListCollection({
    items: [
      { value: "", label: "All Degrees" },
      { value: "BBA", label: "BBA" },
      { value: "MBA", label: "MBA" },
      { value: "EMBA", label: "EMBA" },
      { value: "PHD", label: "PHD" },
    ],
  });

  const handleSearchChange = useCallback((value: string) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setCurrentPage(1);
  }, []);

  const handleMembershipTypeChange = useCallback((membershipTypeId: string) => {
    setFilters((prev) => ({ ...prev, membershipTypeId }));
    setCurrentPage(1);
  }, []);

  const handleCountryChange = useCallback((countryId: string) => {
    setFilters((prev) => ({ ...prev, countryId }));
    setCurrentPage(1);
  }, []);

  const handleDegreeChange = useCallback((degree: string) => {
    setFilters((prev) => ({ ...prev, degree }));
    setCurrentPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({ search: "", membershipTypeId: "", countryId: "", degree: "" });
    setCurrentPage(1);
  }, []);

  const totalPages = Math.ceil((membersData?.total || 0) / PAGE_SIZE);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;
  const members = membersData?.items || [];

  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Box
          p={4}
          bg="red.50"
          border="1px solid"
          borderColor="red.200"
          borderRadius="md"
        >
          <Text color="red.800">
            Failed to load members. Please try again later.
          </Text>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack gap={6} align="stretch">
        {/* Header */}
        <Box>
          <Heading size="2xl" mb={2} color="gray.800">
            Member Search
          </Heading>
          <Text color="gray.600">
            Search and browse through our alumni community
          </Text>
        </Box>

        {/* Search and Filter Section */}
        <Box
          bg="white"
          p={6}
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.200"
        >
          <VStack gap={4}>
            {/* Search Input */}
            <Box position="relative" w="full">
              <Input
                placeholder="Search by name, membership number, degree, batch, profession..."
                value={filters.search}
                onChange={(e) => handleSearchChange(e.target.value)}
                pl={10}
                borderRadius={"lg"}
                py={5}
                colorPalette={"blue"}
                outline={"none"}
              />
              <Icon
                position="absolute"
                left={3}
                top="50%"
                transform="translateY(-50%)"
                colorPalette={"gray"}
              >
                <LuSearch color="gray.300" />
              </Icon>
            </Box>

            {/* Filter Options */}
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
              gap={4}
              w="full"
            >
              {/* Country Filter */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.600">
                  Country (Residential Address)
                </Text>
                <ReactSelect
                  options={countryOptions}
                  value={
                    countryOptions.find(
                      (option) => option.value === filters.countryId
                    ) || null
                  }
                  onChange={(selectedOption: any) =>
                    handleCountryChange(selectedOption?.value || "")
                  }
                  placeholder="Select Country"
                  isClearable
                  isSearchable
                />
              </Box>

              {/* Membership Type Filter */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.600">
                  Membership Type
                </Text>
                <Select.Root
                  value={
                    filters.membershipTypeId ? [filters.membershipTypeId] : []
                  }
                  onValueChange={({ value }) => {
                    const selectedValue = value[0] || "";
                    handleMembershipTypeChange(selectedValue);
                  }}
                  collection={membershipTypesCollection}
                >
                  <Select.HiddenSelect />
                  <Select.Control>
                    <Select.Trigger>
                      <Select.ValueText placeholder="All Membership Types" />
                    </Select.Trigger>
                    <Select.IndicatorGroup>
                      <Select.Indicator />
                    </Select.IndicatorGroup>
                  </Select.Control>
                  <Portal>
                    <Select.Positioner>
                      <Select.Content>
                        {membershipTypesCollection.items.map((item) => (
                          <Select.Item key={item.value} item={item}>
                            {item.label}
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Portal>
                </Select.Root>
              </Box>

              {/* Degree Filter */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.600">
                  IBA Degree
                </Text>
                <Select.Root
                  collection={degreeCollection}
                  value={filters.degree ? [filters.degree] : []}
                  onValueChange={(details) =>
                    handleDegreeChange(details.value[0] || "")
                  }
                >
                  <Select.HiddenSelect />
                  <Select.Control>
                    <Select.Trigger>
                      <Select.ValueText placeholder="Select Degree" />
                    </Select.Trigger>
                    <Select.IndicatorGroup>
                      <Select.Indicator />
                    </Select.IndicatorGroup>
                  </Select.Control>
                  <Portal>
                    <Select.Positioner>
                      <Select.Content>
                        {degreeCollection.items.map((item) => (
                          <Select.Item item={item} key={item.value}>
                            {item.label}
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Portal>
                </Select.Root>
              </Box>
            </Grid>

            {/* Active Filters and Clear Button */}
            {(filters.search ||
              filters.membershipTypeId ||
              filters.countryId ||
              filters.degree) && (
              <HStack gap={2} w="full" justify="space-between">
                <HStack gap={2} flex={1} flexWrap="wrap">
                  {filters.search && (
                    <Badge variant="solid" colorPalette="blue">
                      Search: "{filters.search}"
                    </Badge>
                  )}
                  {filters.countryId && (
                    <Badge variant="solid" colorPalette="purple">
                      Country:{" "}
                      {
                        countriesData?.find(
                          (c) => c.id.toString() === filters.countryId
                        )?.name
                      }
                    </Badge>
                  )}
                  {filters.membershipTypeId && (
                    <Badge variant="solid" colorPalette="green">
                      {
                        membershipTypes?.find(
                          (t) => t.id === filters.membershipTypeId
                        )?.name
                      }
                    </Badge>
                  )}
                  {filters.degree && (
                    <Badge variant="solid" colorPalette="orange">
                      Degree: {filters.degree}
                    </Badge>
                  )}
                </HStack>
                <IconButton
                  size="sm"
                  colorPalette={"red"}
                  variant={"outline"}
                  onClick={clearFilters}
                >
                  <Icon as={MdClear} />
                </IconButton>
              </HStack>
            )}
          </VStack>
        </Box>

        {/* Results Summary */}
        <HStack justify="space-between" align="center">
          <Text color="gray.600">
            {isLoading
              ? "Loading..."
              : `Showing ${(currentPage - 1) * PAGE_SIZE + 1} to ${Math.min(
                  currentPage * PAGE_SIZE,
                  membersData?.total || 0
                )} of ${membersData?.total || 0} members`}
          </Text>

          {isFetching && <GlobalLoading />}
        </HStack>

        {/* Members Grid */}
        {isLoading ? (
          <Center py={20}>
            <VStack gap={4}>
              <Spinner size="xl" />
              <Text>Loading members...</Text>
            </VStack>
          </Center>
        ) : members.length === 0 ? (
          <Center py={20}>
            <VStack gap={4}>
              <Text fontSize="xl" color="gray.500">
                No members found
              </Text>
              <Text color="gray.400">Try adjusting your search criteria</Text>
            </VStack>
          </Center>
        ) : (
          <Grid
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
              xl: "repeat(3, 1fr)",
            }}
            gap={6}
          >
            {members.map((member) => (
              <MemberCard key={member.id} member={member} />
            ))}
          </Grid>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Flex justify="center" align="center" gap={4}>
            <IconButton
              aria-label="Previous page"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={!hasPrevPage || isLoading}
              variant="outline"
            >
              <LuChevronLeft />
            </IconButton>

            <HStack gap={2}>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNumber;
                if (totalPages <= 5) {
                  pageNumber = i + 1;
                } else if (currentPage <= 3) {
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + i;
                } else {
                  pageNumber = currentPage - 2 + i;
                }

                return (
                  <Button
                    key={pageNumber}
                    size="sm"
                    variant={currentPage === pageNumber ? "solid" : "outline"}
                    colorPalette={currentPage === pageNumber ? "blue" : "gray"}
                    onClick={() => setCurrentPage(pageNumber)}
                    disabled={isLoading}
                  >
                    {pageNumber}
                  </Button>
                );
              })}
            </HStack>

            <IconButton
              aria-label="Next page"
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={!hasNextPage || isLoading}
              variant="outline"
            >
              <LuChevronRight />
            </IconButton>
          </Flex>
        )}
      </VStack>
    </Container>
  );
}

export default MemberForAllMember;
